import { Controller } from "@hotwired/stimulus";

const HOVER_OPEN_DELAY_MS = 250;

export default class extends Controller {
  static targets = ["trigger", "portal", "template"];
  static values = { origin: String, url: String };

  connect() {
    if (!this.hasTriggerTarget || !this.hasPortalTarget) return;

    this.triggerElement = this.triggerTarget;
    this.portalElement = this.portalTarget;
    this.panelElement = this.portalElement.querySelector("[data-assignment-lifecycle-popover-panel]");
    this.portalHomeElement = this.portalElement.parentElement;
    this.openTimeout = null;
    this.closeTimeout = null;
    this.isOpen = false;
    this.openRequested = false;

    this.scheduleOpen = this.scheduleOpen.bind(this);
    this.open = this.open.bind(this);
    this.scheduleClose = this.scheduleClose.bind(this);
    this.keepOpen = this.keepOpen.bind(this);
    this.close = this.close.bind(this);
    this.closeOnOutsidePointerDown = this.closeOnOutsidePointerDown.bind(this);
    this.reposition = this.reposition.bind(this);
    this.closeOnEscape = this.closeOnEscape.bind(this);
    this.prepareForClone = this.prepareForClone.bind(this);
    this.ready = true;
    this.panelRequest = null;

    this.element.addEventListener("mouseenter", this.scheduleOpen);
    this.element.addEventListener("mouseleave", this.scheduleClose);
    this.element.addEventListener("focusin", this.open);
    this.element.addEventListener("focusout", this.scheduleClose);
    this.portalElement.addEventListener("mouseenter", this.keepOpen);
    this.portalElement.addEventListener("mouseleave", this.scheduleClose);
    this.triggerElement.addEventListener("click", this.open);
    this.element.addEventListener("dnd:before-clone", this.prepareForClone);
  }

  disconnect() {
    if (!this.ready) return;

    this.close();

    this.element.removeEventListener("mouseenter", this.scheduleOpen);
    this.element.removeEventListener("mouseleave", this.scheduleClose);
    this.element.removeEventListener("focusin", this.open);
    this.element.removeEventListener("focusout", this.scheduleClose);
    this.portalElement.removeEventListener("mouseenter", this.keepOpen);
    this.portalElement.removeEventListener("mouseleave", this.scheduleClose);
    this.triggerElement.removeEventListener("click", this.open);
    this.element.removeEventListener("dnd:before-clone", this.prepareForClone);

    this.portalElement.remove();
    this.ready = false;
  }

  prepareForClone(event) {
    const source = event?.detail?.source;
    if (source instanceof Element && !source.contains(this.element)) return;

    this.close();
  }

  scheduleOpen() {
    this.clearCloseTimeout();
    if (this.isOpen || this.openTimeout) return;

    this.openTimeout = window.setTimeout(() => {
      this.openTimeout = null;
      this.open();
    }, HOVER_OPEN_DELAY_MS);
  }

  async open() {
    this.clearOpenTimeout();
    this.clearCloseTimeout();
    this.openRequested = true;
    await this.materializePanel();
    if (!this.panelElement || !this.ready || !this.openRequested) return;

    if (this.portalElement.parentElement !== document.body) {
      document.body.appendChild(this.portalElement);
    }

    this.portalElement.classList.remove("hidden");
    this.portalElement.dataset.assignmentLifecyclePopoverOpen = "true";
    this.portalElement.style.position = "fixed";
    this.portalElement.style.zIndex = "1100";
    this.portalElement.style.isolation = "isolate";
    this.triggerElement.setAttribute("aria-expanded", "true");
    if (!this.isOpen) {
      document.addEventListener("pointerdown", this.closeOnOutsidePointerDown, true);
      document.addEventListener("keydown", this.closeOnEscape);
      document.addEventListener("turbo:before-cache", this.close);
      window.addEventListener("resize", this.reposition);
      window.addEventListener("scroll", this.reposition, true);
      this.isOpen = true;
    }
    this.reposition();
  }

  async materializePanel() {
    if (this.panelElement) return;

    if (this.hasTemplateTarget) {
      this.portalElement.appendChild(this.templateTarget.content.cloneNode(true));
      this.panelElement = this.portalElement.querySelector("[data-assignment-lifecycle-popover-panel]");
      return;
    }

    if (!this.hasUrlValue) return;

    if (!this.panelRequest) {
      this.triggerElement.setAttribute("aria-busy", "true");
      this.panelRequest = fetch(this.urlValue, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Lifecycle popover request failed with ${response.status}`);

        this.portalElement.innerHTML = await response.text();
        this.panelElement = this.portalElement.querySelector("[data-assignment-lifecycle-popover-panel]");
      }).catch((error) => {
        console.error(error);
        this.panelRequest = null;
      }).finally(() => {
        this.triggerElement.removeAttribute("aria-busy");
      });
    }

    await this.panelRequest;
  }

  scheduleClose(event) {
    const relatedTarget = event?.relatedTarget;
    if (relatedTarget instanceof Node && (this.element.contains(relatedTarget) || this.portalElement.contains(relatedTarget))) {
      return;
    }

    this.openRequested = false;
    this.clearOpenTimeout();
    this.clearCloseTimeout();
    this.closeTimeout = window.setTimeout(this.close, 90);
  }

  keepOpen() {
    this.clearCloseTimeout();
  }

  close() {
    this.openRequested = false;
    this.clearOpenTimeout();
    this.clearCloseTimeout();
    this.portalElement.classList.add("hidden");
    delete this.portalElement.dataset.assignmentLifecyclePopoverOpen;
    this.triggerElement.setAttribute("aria-expanded", "false");
    if (this.isOpen) {
      document.removeEventListener("pointerdown", this.closeOnOutsidePointerDown, true);
      document.removeEventListener("keydown", this.closeOnEscape);
      document.removeEventListener("turbo:before-cache", this.close);
      window.removeEventListener("resize", this.reposition);
      window.removeEventListener("scroll", this.reposition, true);
      this.isOpen = false;
    }
    if (this.portalHomeElement && this.portalElement.parentElement !== this.portalHomeElement) {
      this.portalHomeElement.appendChild(this.portalElement);
    }
  }

  closeOnOutsidePointerDown(event) {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.element.contains(target) || this.portalElement.contains(target)) return;

    this.close();
  }

  closeOnEscape(event) {
    if (event.key !== "Escape" || this.portalElement.classList.contains("hidden")) return;

    this.close();
    this.triggerElement.focus();
  }

  reposition() {
    if (this.portalElement.classList.contains("hidden")) return;

    const anchorElement = this.triggerElement.querySelector("[data-assignment-lifecycle-indicator]") || this.triggerElement;
    const triggerRect = anchorElement.getBoundingClientRect();
    const margin = 8;
    const gap = 2;
    const availableHeight = Math.max(0, window.innerHeight - (margin * 2));

    this.portalElement.style.maxHeight = `${availableHeight}px`;
    this.portalElement.style.left = "0px";
    this.portalElement.style.top = "0px";
    this.portalElement.style.paddingTop = "0px";
    this.portalElement.style.paddingBottom = "0px";

    const portalRect = this.portalElement.getBoundingClientRect();
    const panelContentRect = (this.panelElement || this.portalElement).getBoundingClientRect();
    const visiblePanelHeight = Math.min(panelContentRect.height, Math.max(0, availableHeight - gap));
    const portalHeight = visiblePanelHeight + gap;
    const belowTop = triggerRect.bottom;
    const aboveTop = triggerRect.top - portalHeight;
    const useBelow = belowTop + portalHeight <= window.innerHeight - margin || aboveTop < margin;
    const top = useBelow ?
      Math.min(belowTop, Math.max(margin, window.innerHeight - portalHeight - margin)) :
      Math.max(margin, aboveTop);
    const left = this.originValue === "canvas" ?
      this.canvasLeft(triggerRect, portalRect, margin, gap) :
      Math.min(
        Math.max(triggerRect.left, margin),
        Math.max(margin, window.innerWidth - portalRect.width - margin)
      );

    this.portalElement.style.left = `${left}px`;
    this.portalElement.style.top = `${top}px`;
    this.portalElement.style.paddingTop = useBelow ? `${gap}px` : "0px";
    this.portalElement.style.paddingBottom = useBelow ? "0px" : `${gap}px`;
  }

  canvasLeft(triggerRect, portalRect, margin, gap) {
    const rightLeft = triggerRect.right + gap;
    const leftLeft = triggerRect.left - portalRect.width - gap;
    const maxLeft = Math.max(margin, window.innerWidth - portalRect.width - margin);

    if (rightLeft + portalRect.width <= window.innerWidth - margin || leftLeft < margin) {
      return Math.min(Math.max(rightLeft, margin), maxLeft);
    }

    return Math.min(Math.max(leftLeft, margin), maxLeft);
  }

  clearOpenTimeout() {
    if (!this.openTimeout) return;

    window.clearTimeout(this.openTimeout);
    this.openTimeout = null;
  }

  clearCloseTimeout() {
    if (!this.closeTimeout) return;

    window.clearTimeout(this.closeTimeout);
    this.closeTimeout = null;
  }
}
